import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Gift, Star, Calendar, CreditCard, Shield, ChevronRight, 
  Sparkles, Package, ShoppingBag, Code, TrendingUp, BookOpen, Video,
  Award, Users, Smartphone, Monitor, FileText, Headphones, ShoppingCart,
  Shirt, Coffee, Package2, Palette, Clock, Zap, BarChart, Download,
  Globe, DollarSign, MessageSquare, Youtube, Mail, Image, Timer,
  TreePine, Heart, Ghost, Gem, AlertCircle, Play, TrendingDown,
  Building, UserPlus, FolderOpen, Eye, FileText as Report, Settings
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const UpgradeAgency = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes countdown
  const [showUrgencyBanner, setShowUrgencyBanner] = useState(false);
  const [spotsLeft, setSpotsLeft] = useState(23); // Starting spots

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 180 && !showUrgencyBanner) { // Show urgency at 3 minutes
          setShowUrgencyBanner(true);
        }
        return prevTime > 0 ? prevTime - 1 : 0;
      });
    }, 1000);

    // Simulate spots being taken
    const spotsTimer = setInterval(() => {
      setSpotsLeft((prev) => {
        const decrement = Math.random() > 0.8 ? 1 : 0;
        return prev > 5 ? prev - decrement : prev;
      });
    }, 20000); // Every 20 seconds

    return () => {
      clearInterval(timer);
      clearInterval(spotsTimer);
    };
  }, [showUrgencyBanner]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUpgrade = async () => {
    setIsProcessing(true);
    try {
      // TODO: Implement actual upgrade logic with payment processing
      console.log('Processing Agency License upgrade...');
      // Redirect to payment processor or handle upgrade
    } catch (error) {
      console.error('Upgrade error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNoThanks = () => {
    // Redirect to dashboard
    navigate('/dashboard');
  };

  return (
    <>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Kalam:wght@400;700&display=swap" rel="stylesheet" />
      
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slide {
          from { transform: translateX(0); }
          to { transform: translateX(20px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        
        .animation-delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
      
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Urgency Banner - Shows when time is low */}
      {showUrgencyBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-4 shadow-lg animate-pulse">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-4 text-sm md:text-base font-bold">
            <AlertCircle className="w-5 h-5" />
            <span>⚠️ FINAL WARNING: Agency License expires in {formatTime(timeLeft)} - Last chance!</span>
            <AlertCircle className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Scarcity Bar - Dark Mode Design */}
      <div className="relative bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white py-4 px-4 overflow-hidden shadow-2xl border-b border-gray-700">
        {/* Animated background effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 via-orange-600/30 to-red-600/20"></div>
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(255, 255, 255, 0.03) 10px,
                rgba(255, 255, 255, 0.03) 20px
              )`,
              animation: 'slide 20s linear infinite'
            }}
          ></div>
        </div>
        
        {/* Enhanced flashing lights effect */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute top-2 left-10 w-3 h-3 bg-red-400 rounded-full animate-ping shadow-lg"></div>
          <div className="absolute top-3 right-20 w-2 h-2 bg-orange-400 rounded-full animate-ping animation-delay-500 shadow-lg"></div>
          <div className="absolute bottom-2 left-1/3 w-2 h-2 bg-yellow-400 rounded-full animate-ping animation-delay-1000 shadow-lg"></div>
          <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-red-500 rounded-full animate-pulse animation-delay-1500"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Spots Counter - Enhanced */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg blur opacity-75 animate-pulse"></div>
                <div className="relative flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 px-5 py-2.5 rounded-lg border border-yellow-400/50 shadow-lg">
                  <div className="flex items-center gap-1">
                    <TrendingDown className="w-5 h-5 text-yellow-300 animate-bounce" />
                    <AlertCircle className="w-4 h-4 text-yellow-300 animate-pulse" />
                  </div>
                  <span className="font-black text-lg text-white drop-shadow-lg">
                    ONLY {spotsLeft} AGENCY LICENSES LEFT
                  </span>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 text-sm text-yellow-200 font-medium">
                <Sparkles className="w-4 h-4" />
                <span>final invitation</span>
              </div>
            </div>

            {/* Progress Bar - Enhanced */}
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <div className="bg-black/50 rounded-full h-4 overflow-hidden shadow-inner">
                  <div 
                    className="relative h-full transition-all duration-500 ease-out rounded-full"
                    style={{ 
                      width: `${((50 - spotsLeft) / 50) * 100}%`,
                      background: `linear-gradient(to right, #10b981 0%, #eab308 ${((50 - spotsLeft) / 50) * 50}%, #ef4444 100%)`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/30"></div>
                    <div className="absolute right-0 top-0 bottom-0 w-2 bg-white/50 animate-pulse"></div>
                  </div>
                </div>
                <p className="text-xs text-yellow-200 mt-1 text-center font-medium">
                  🔥 {50 - spotsLeft} agencies already activated!
                </p>
              </div>
            </div>

            {/* Timer - Enhanced */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg blur opacity-75 animate-pulse"></div>
              <div className="relative flex items-center gap-2 bg-gradient-to-r from-yellow-600 to-orange-600 px-5 py-2.5 rounded-lg border border-yellow-300/50 shadow-lg">
                <Clock className="w-5 h-5 text-white animate-spin-slow drop-shadow" />
                <div>
                  <p className="text-xs text-yellow-100 font-medium">FINAL OFFER EXPIRES</p>
                  <span className="font-black text-xl text-white tabular-nums drop-shadow-lg">
                    {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-orange-600 via-red-600 to-pink-600 text-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full filter blur-3xl"></div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-4 py-12 relative z-10">
          <div className="text-center mb-8">
            {/* Enhanced badge */}
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 backdrop-blur-md px-8 py-4 rounded-full mb-8 animate-bounce border border-yellow-400/30 mx-auto">
              <Building className="w-6 h-6 text-yellow-300" />
              <span className="font-bold text-lg uppercase tracking-wide">YOUR FINAL INVITATION</span>
              <Building className="w-6 h-6 text-yellow-300" />
            </div>
            
            {/* Enhanced headline */}
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight text-center">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-yellow-200 drop-shadow-2xl">
                Stop Being a Freelancer
              </span>
              <span className="block text-3xl md:text-5xl mt-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-orange-300 to-pink-300">
                Start Building an <span className="underline decoration-wavy decoration-4 underline-offset-8">Agency</span>
              </span>
            </h1>
            
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              This is the last upgrade you will see. It is not available to the public.
            </p>
          </div>

          {/* Video Placeholder */}
          <div className="mb-8 animate-fadeIn max-w-4xl mx-auto">
            <div 
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl aspect-video flex items-center justify-center shadow-2xl relative overflow-hidden cursor-pointer hover:shadow-3xl transition-all duration-300 group border border-white/20"
            >
              {/* Animated background pattern */}
              <div className="absolute inset-0 opacity-30">
                <div 
                  className="absolute inset-0 animate-pulse"
                  style={{
                    backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255, 255, 255, 0.03) 35px, rgba(255, 255, 255, 0.03) 70px)`,
                  }}
                ></div>
              </div>
              
              {/* Play button */}
              <div className="text-center z-10 group-hover:scale-110 transition-transform duration-300">
                <div className="w-28 h-28 bg-white rounded-full flex items-center justify-center mb-4 mx-auto shadow-2xl group-hover:shadow-3xl transition-shadow">
                  <Play className="w-14 h-14 text-orange-600 ml-2" />
                </div>
                <p className="text-white font-bold text-2xl mb-2 drop-shadow-lg">Scale Your Business to Agency Level</p>
                <p className="text-orange-100 text-lg drop-shadow">Watch this exclusive agency invitation</p>
              </div>

              {/* Video duration badge */}
              <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur text-white px-4 py-2 rounded-lg font-bold">
                3:47
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction Section */}
        <div className="text-center mb-12">
          <div className="mb-8 relative">
            {/* Decorative sparkles */}
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
              <Sparkles className="w-10 h-10 text-yellow-500 animate-pulse" />
            </div>
            <div className="absolute top-0 left-10 md:left-20">
              <Star className="w-6 h-6 text-orange-400 animate-spin-slow" />
            </div>
            <div className="absolute top-10 right-10 md:right-20">
              <Sparkles className="w-6 h-6 text-pink-400 animate-pulse animation-delay-500" />
            </div>
            
            <h2 
              className="text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 mb-6 leading-tight animate-float"
              style={{ fontFamily: "'Caveat', cursive", fontWeight: 700 }}
            >
              You've Made Smart Decisions Today!
            </h2>
            
            <div 
              className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed space-y-4"
              style={{ fontFamily: "'Kalam', cursive" }}
            >
              <p>You secured the core BrandScapes AI engine, you upgraded to the Professional toolkit, and you armed yourself with our entire library of niche and holiday templates.</p>
              <p><span className="text-orange-600 font-bold">You now have a complete arsenal</span> for creating world-class visuals.</p>
            </div>
            
            <p className="text-xl text-gray-800 font-semibold mb-6 max-w-2xl mx-auto">
              The question is no longer <em>if</em> you can create stunning designs. 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600"> The question is, how much money can you make with them?</span>
            </p>
          </div>
        </div>
      </div>

      {/* The Freelancer's Ceiling - Full Width Gradient */}
      <div className="relative py-20 mb-16 bg-gradient-to-br from-red-700 via-orange-600 to-pink-600 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        {/* Floating Decorative Elements */}
        <div className="absolute top-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-float animation-delay-1000"></div>
        <div className="absolute top-1/2 right-1/3 w-28 h-28 bg-yellow-400/20 rounded-full blur-xl animate-pulse animation-delay-500"></div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          {/* Main Card */}
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-12 text-center">
            {/* Decorative Elements */}
            <div className="absolute top-6 left-6 w-12 h-12 bg-gradient-to-br from-red-400 to-orange-400 rounded-full opacity-20"></div>
            <div className="absolute bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-orange-400 to-pink-400 rounded-full opacity-20"></div>
            
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-8 leading-tight">
                Right Now, You're a 
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-600 to-pink-600">
                  Highly-Skilled Operator
                </span>
                <span className="block text-2xl md:text-3xl mt-4 text-gray-700">
                  But You're Still Just One Person
                </span>
              </h2>
              
              <div className="max-w-4xl mx-auto space-y-8">
                <p className="text-xl text-gray-700 leading-relaxed">
                  Your income is capped by the number of hours in your day. This is the <strong>Freelancer's Ceiling.</strong>
                </p>
                
                {/* Scaling Requirements */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border-2 border-orange-200">
                  <p className="text-2xl font-bold text-gray-800 mb-4">
                    To truly grow, you need:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Zap className="w-6 h-6 text-orange-600" />
                      <span className="text-lg font-semibold text-gray-700">Leverage</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Settings className="w-6 h-6 text-red-600" />
                      <span className="text-lg font-semibold text-gray-700">Systems</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="w-6 h-6 text-pink-600" />
                      <span className="text-lg font-semibold text-gray-700">A Team</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building className="w-6 h-6 text-orange-600" />
                      <span className="text-lg font-semibold text-gray-700">Agency Operations</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-800">
                    From our office here in Timișoara, on {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}, 
                    <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                      we are inviting you to make that leap.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">

        {/* Agency Operating System Header */}
        <div className="text-center mb-16">
          {/* Enhanced Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 px-6 py-3 rounded-full mb-8 border border-orange-200">
            <Building className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-bold text-orange-700 uppercase tracking-wider">Agency Operating System</span>
          </div>
          
          {/* Enhanced Headline */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-800 mb-6 leading-tight">
            Here is the Agency 
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-red-600 to-pink-600">
              "Operating System"
            </span>
            <span className="block text-2xl md:text-3xl mt-4 text-gray-600 font-bold">
              You're Unlocking:
            </span>
          </h2>
          
          {/* Enhanced Description */}
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              This isn't just a feature pack. This is a suite of integrated tools designed to solve the biggest headaches of running a creative service business.
            </p>
          </div>
        </div>

        {/* Agency Features */}
        <div className="mb-16">
          {/* Feature 1: Client Management */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-orange-400 to-red-400 p-4 rounded-2xl shadow-lg">
                    <Building className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">1. The Client Management & Professionalism Suite</h3>
                    <p className="text-gray-600">Organize, impress, and retain clients like a pro</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="grid md:grid-cols-1 gap-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                        <FolderOpen className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Dedicated Client Management Dashboard</h4>
                        <p className="text-gray-600">Get an "Agency" tab in your account to add, organize, and manage all your clients from one central hub.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="bg-red-100 p-2 rounded-lg flex-shrink-0">
                        <Package className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Client Project Workspaces & Folders</h4>
                        <p className="text-gray-600">Create separate, sandboxed workspaces for each client to keep their projects, assets, and mockups perfectly organized.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="bg-pink-100 p-2 rounded-lg flex-shrink-0">
                        <Star className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Client-Specific Branding</h4>
                        <p className="text-gray-600">Apply your client's logo and branding to their review pages. When they see the work, they see their brand.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: Workflow Automation */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-red-400 to-pink-400 p-4 rounded-2xl shadow-lg">
                    <Zap className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">2. The Workflow Automation Engine</h3>
                    <p className="text-gray-600">End the email chaos and streamline approvals</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="grid md:grid-cols-1 gap-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                        <Eye className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">One-Click Client Feedback & Approval Links</h4>
                        <p className="text-gray-600">End the nightmare of back-and-forth email chains. Generate unique links for clients to view, comment, and approve work instantly.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="bg-red-100 p-2 rounded-lg flex-shrink-0">
                        <Report className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Project Analytics & Exportable Reports</h4>
                        <p className="text-gray-600">Generate professional PDF reports for each client showing work completed and credits used. Justify your invoices and prove your value.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 3: Scaling Toolkit */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-orange-400 to-pink-400 p-4 rounded-2xl shadow-lg">
                    <Users className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">3. The Scaling & Delegation Toolkit</h3>
                    <p className="text-gray-600">Build your team and multiply your output</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="grid md:grid-cols-1 gap-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                        <UserPlus className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Team Member Collaboration (Up to 5 Seats)</h4>
                        <p className="text-gray-600">Invite up to 5 team members or VAs to your account. Assign them to client projects while you focus on getting more clients.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="bg-red-100 p-2 rounded-lg flex-shrink-0">
                        <CreditCard className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 mb-1">Massive Credit Injection (1,100 Agency Credits)</h4>
                        <p className="text-gray-600">We're depositing 1,100 additional credits into your account. The largest credit pack we offer for high-volume agency work.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 4: VIP Treatment */}
          <div className="mb-12 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Top Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
              
              {/* Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-br from-red-400 to-orange-400 p-4 rounded-2xl shadow-lg">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">4. The VIP Treatment</h3>
                    <p className="text-gray-600">Priority support and personal onboarding</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                      <Headphones className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">Dedicated Agency Support & Onboarding</h4>
                      <p className="text-gray-600">You're now a priority. Get access to our dedicated agency support queue and a one-time, 30-minute personal onboarding call.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Value Stack Section */}
      <div className="relative py-20 mb-16 bg-gradient-to-br from-purple-700 via-pink-600 to-red-600 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
            }}
          ></div>
        </div>
        
        {/* Floating Decorative Elements */}
        <div className="absolute top-10 left-10 w-36 h-36 bg-white/10 rounded-full blur-2xl animate-float"></div>
        <div className="absolute bottom-10 right-10 w-44 h-44 bg-white/10 rounded-full blur-2xl animate-float animation-delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-yellow-400/20 rounded-full blur-xl animate-pulse animation-delay-500"></div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          {/* Main Card */}
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-12 text-center">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-black text-gray-800 mb-8 leading-tight">
                The Choice: Stay a 
                <span className="text-gray-500">Freelancer</span>, or Become 
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-red-600">
                  The Agency
                </span>
              </h2>
              
              <div className="max-w-4xl mx-auto space-y-8">
                <p className="text-xl text-gray-700 leading-relaxed">
                  Let's be blunt. The value here is immense. A client management system alone costs upwards of $50/month. You're getting the tools, the credits, the team seats, and the support to build a real business.
                </p>
                
                {/* Value Stack */}
                <div className="bg-gray-50 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">Here's the value stack:</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-orange-400 to-red-400 p-2 rounded-lg">
                          <Building className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">Dedicated Client Management System</span>
                      </span>
                      <span className="text-gray-600 font-semibold">Real Value: $997/yr</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-red-400 to-pink-400 p-2 rounded-lg">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">5 Team Member Seats</span>
                      </span>
                      <span className="text-gray-600 font-semibold">Real Value: $497/yr</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-orange-400 to-pink-400 p-2 rounded-lg">
                          <CreditCard className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">1,100 Agency Credits</span>
                      </span>
                      <span className="text-gray-600 font-semibold">Real Value: $197</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-red-400 to-orange-400 p-2 rounded-lg">
                          <Eye className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">Client Feedback & Approval System</span>
                      </span>
                      <span className="text-gray-600 font-semibold">Real Value: $297/yr</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-orange-400 to-red-400 p-2 rounded-lg">
                          <BarChart className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">Analytics & Reporting Suite</span>
                      </span>
                      <span className="text-gray-600 font-semibold">Real Value: $197/yr</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-pink-400 to-orange-400 p-2 rounded-lg">
                          <Headphones className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-gray-800 text-lg font-medium">Personal Agency Onboarding Call</span>
                      </span>
                      <span className="text-gray-600 font-semibold">Real Value: $297</span>
                    </div>
                    
                    {/* Total Value */}
                    <div className="mt-6 pt-6 border-t-2 border-gray-300">
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-gray-800">Total Real-World Value:</span>
                        <div className="text-right">
                          <span className="text-4xl font-bold text-red-500 line-through">$2,482</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Pricing */}
                <div className="text-center mt-8">
                  <p className="text-xl text-gray-600 mb-4">
                    You are not going to pay anywhere near that. For new Founders on this page only, you can unlock the complete Agency License for a single, final payment.
                  </p>
                  
                  {/* Price Display */}
                  <div className="inline-block bg-gradient-to-r from-orange-500 to-red-500 p-1 rounded-3xl">
                    <div className="bg-white rounded-3xl px-12 py-8 shadow-inner">
                      <div className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 text-8xl font-black">$197</div>
                      <p className="text-gray-600 text-lg mt-2 font-semibold">Final Payment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mb-12">
          <button
            onClick={handleUpgrade}
            disabled={isProcessing}
            className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white text-xl font-bold py-6 px-12 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 mb-4 inline-flex items-center gap-3 relative group"
          >
            {/* Button pulse effect */}
            <span className="absolute -inset-1 bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 rounded-2xl opacity-75 blur-lg group-hover:opacity-100 transition-opacity animate-pulse"></span>
            
            <span className="relative flex items-center gap-3">
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Building className="w-6 h-6" />
                  YES! Activate My Agency License For Just $197
                </>
              )}
            </span>
          </button>
          
          {/* Countdown Timer Under CTA */}
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg">
              <Timer className="w-4 h-4 animate-pulse" />
              <span className="font-bold">Final offer expires in: <span className="tabular-nums">{formatTime(timeLeft)}</span></span>
            </div>
          </div>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-gray-500" />
            <p className="text-sm text-gray-600">This final purchase is also 100% protected by our 30-Day Money-Back Guarantee</p>
          </div>
        </div>
        </div>
      </div>

      {/* No Thank You Link - Bottom of Page */}
      <div className="text-center mb-12">
        <button
          onClick={handleNoThanks}
          className="text-gray-500 hover:text-gray-700 underline text-sm max-w-4xl mx-auto leading-relaxed transition-colors duration-200"
        >
          No, thank you. I understand this is my only chance to get the Agency License. I am happy to remain a single user and will figure out how to manage clients and scale my business on my own. I understand I am forfeiting this offer forever.
        </button>
      </div>

      {/* FOOTER - Professional Design */}
      <footer className="relative bg-gray-900 text-gray-300 mt-20">
        {/* Top border accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500"></div>
        
        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Logo and Company Info */}
          <div className="text-center mb-12">
            <img 
              src="/clawbrand_logo_color.png" 
              alt="ClawBrand AI" 
              className="h-16 mx-auto mb-6"
            />
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Transform your marketing with AI-powered mockups. Create stunning visuals in seconds, not hours.
            </p>
          </div>
            
          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-12 text-sm">
            <a href="/support" className="hover:text-white transition-colors duration-200">Support</a>
            <span className="text-gray-600">•</span>
            <a href="/privacy" className="hover:text-white transition-colors duration-200">Privacy Policy</a>
            <span className="text-gray-600">•</span>
            <a href="/refund" className="hover:text-white transition-colors duration-200">Refund Policy</a>
            <span className="text-gray-600">•</span>
            <a href="/cookies" className="hover:text-white transition-colors duration-200">Cookie Policy</a>
            <span className="text-gray-600">•</span>
            <a href="/terms" className="hover:text-white transition-colors duration-200">Terms of Service</a>
            <span className="text-gray-600">•</span>
            <a href="/disclaimer" className="hover:text-white transition-colors duration-200">Disclaimer</a>
          </div>
          
          {/* Disclaimers */}
          <div className="space-y-6 text-xs text-gray-500 max-w-4xl mx-auto">
            {/* Facebook Disclaimer */}
            <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
              <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">Facebook Disclaimer</h4>
              <p className="leading-relaxed">
                In no way are we associated with Facebook. Once you leave Facebook the responsibility is no longer on any Instagram owned entity.
                Your information and privacy is totally secure and will NEVER be shared. Facebook is in NO WAY associated with this website or anything here.
              </p>
            </div>
            
            {/* General Disclaimer */}
            <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
              <h4 className="font-semibold text-gray-400 mb-2 uppercase tracking-wider">General Disclaimer</h4>
              <p className="leading-relaxed">
                Please note that this product does not provide any guarantee of income or success. The results achieved by the product owner or any other individuals mentioned are not indicative of future success or earnings. This website is not affiliated with FaceBook or any of its associated entities. Once you navigate away from FaceBook, the responsibility for the content and its usage lies solely with the user. All content on this website, including but not limited to text, images, and multimedia, is protected by copyright law and the Digital Millennium Copyright Act. Unauthorized copying, duplication, modification, or theft, whether intentional or unintentional, is strictly prohibited. Violators will be prosecuted to the fullest extent of the law. We want to clarify that JVZoo serves as the retailer for the products featured on this site. JVZoo® is a registered trademark of BBC Systems Inc., a Florida corporation located at 1809 E. Broadway Street, Suite 125, Oviedo, FL 32765, USA, and is used with permission. The role of JVZoo as a retailer does not constitute an endorsement, approval, or review of these products or any claims, statements, or opinions used in their promotion.
              </p>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="text-center mt-12 pt-8 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} BrandScapes AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default UpgradeAgency;
